import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CameraCapture from '../components/CameraCapture';
import AnnotationCanvas, {
  AnnotationCanvasHandle,
  AngleMode,
} from '../components/AnnotationCanvas';
import IconButton from '../components/IconButton';
import {
  CloseIcon,
  RetakeIcon,
  SaveIcon,
  ExportIcon,
} from '../components/UIIcons';
import {
  exportCanvasJpeg,
  saveCanvasLocal,
  ExportCancelledError,
} from '../lib/exportImage';
import { haptic } from '../lib/haptics';
import { fmtDeg } from '../lib/geometry';
import { buildAngleReport } from '../lib/angleReport';
import { safeTopPad, safeBottomPad, belowTopBar } from '../lib/safeArea';

type Phase = 'capture' | 'annotate';

export default function AnglePage() {
  const nav = useNavigate();
  const [phase, setPhase] = useState<Phase>('capture');
  const [frame, setFrame] = useState<HTMLCanvasElement | null>(null);
  const [angle, setAngle] = useState(0);
  const [mode, setMode] = useState<AngleMode>('free');
  const [savedHint, setSavedHint] = useState<string | null>(null);
  const annotRef = useRef<AnnotationCanvasHandle>(null);

  const handleCapture = (canvas: HTMLCanvasElement) => {
    void haptic('tap');
    setFrame(canvas);
    setPhase('annotate');
  };

  const retake = () => {
    setFrame(null);
    setPhase('capture');
  };

  const flashHint = (msg: string) => {
    setSavedHint(msg);
    setTimeout(() => setSavedHint(null), 2200);
  };

  const exportImage = async () => {
    if (!annotRef.current) return;
    try {
      const annotated = annotRef.current.flatten();
      const report = buildAngleReport({
        annotated,
        angleDeg: angle,
        mode,
        timestamp: Date.now(),
      });
      await exportCanvasJpeg(report, `cadtomie-angle-${Date.now()}.jpg`, 0.94);
      void haptic('success');
    } catch (err) {
      if (err instanceof ExportCancelledError) {
        // User dismissed the share sheet — not a real failure, no toast.
        return;
      }
      console.error('[angle] Export failed', err);
      void haptic('error');
      flashHint('Export failed — try again');
    }
  };

  const saveLocal = async () => {
    if (!annotRef.current) return;
    try {
      const annotated = annotRef.current.flatten();
      const report = buildAngleReport({
        annotated,
        angleDeg: angle,
        mode,
        timestamp: Date.now(),
      });
      const key = await saveCanvasLocal(report);
      flashHint(`Saved (${key})`);
    } catch (err) {
      console.error('[angle] Save failed', err);
      void haptic('error');
      flashHint('Save failed — try again');
    }
  };

  if (phase === 'capture') {
    return <CameraCapture onCapture={handleCapture} onCancel={() => nav('/app')} />;
  }

  if (!frame) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000' }}>
      <AnnotationCanvas
        ref={annotRef}
        source={frame}
        mode={mode}
        onAngleChange={setAngle}
      />

      {/* Top row — close, angle HUD, reset.
          NOTE: padding-top uses an inline max(env(safe-area-inset-top), 12px)
          so the bar always clears the Dynamic Island / notch in standalone
          PWA mode. The CSS `.safe-top` class is intentionally NOT used here
          because the inline `padding-*` longhands below would otherwise be
          overridden by it (and vice versa). See lib/safeArea.ts. */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          paddingTop: safeTopPad(12),
          paddingBottom: 12,
          paddingLeft: 16,
          paddingRight: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0))',
          pointerEvents: 'none',
        }}
      >
        <div style={{ pointerEvents: 'auto' }}>
          <IconButton
            label="Close"
            variant="ghost"
            onClick={() => nav('/app')}
            icon={<CloseIcon />}
          />
        </div>

        <div
          aria-live="polite"
          style={{
            pointerEvents: 'auto',
            background: 'var(--surface-strong)',
            padding: '10px 18px',
            borderRadius: 999,
            border: '1px solid var(--hairline)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: 0.3,
          }}
        >
          {fmtDeg(angle, 1)}
        </div>

        <div style={{ pointerEvents: 'auto' }}>
          <IconButton
            label="Reset"
            variant="ghost"
            onClick={() => annotRef.current?.reset()}
          />
        </div>
      </div>

      {/* Mode toggle — pill segmented control just below the angle HUD.
          `belowTopBar` keeps this in sync with the actual top-bar
          height even when the safe-area inset changes (rotation,
          standalone vs tab). */}
      <div
        style={{
          position: 'absolute',
          top: belowTopBar(64, 12),
          left: '50%',
          transform: 'translateX(-50%)',
          pointerEvents: 'auto',
        }}
      >
        <SegmentedControl
          value={mode}
          onChange={setMode}
          options={[
            { value: 'free', label: 'Free' },
            { value: 'shared', label: 'Shared vertex' },
          ]}
        />
      </div>

      {/* Bottom action bar — uses inline safe-area math so the
          padding-bottom is never overridden by other inline
          padding. See lib/safeArea.ts. */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          paddingTop: 12,
          paddingBottom: safeBottomPad(16),
          paddingLeft: 16,
          paddingRight: 16,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          background:
            'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))',
        }}
      >
        <IconButton
          label="Retake"
          variant="default"
          onClick={retake}
          icon={<RetakeIcon />}
        />
        <IconButton
          label="Save"
          variant="default"
          onClick={saveLocal}
          icon={<SaveIcon />}
        />
        <IconButton
          label="Export"
          variant="primary"
          size="lg"
          onClick={exportImage}
          icon={<ExportIcon />}
        />
      </div>

      {savedHint && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'var(--surface-strong)',
            padding: '12px 18px',
            borderRadius: 14,
            border: '1px solid var(--hairline)',
            color: 'var(--good)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            pointerEvents: 'none',
            fontSize: 15,
          }}
        >
          {savedHint}
        </div>
      )}
    </div>
  );
}

/* ─── small inline segmented control (Apple-ish) ─── */

interface SegOption<T extends string> {
  value: T;
  label: string;
}

function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: SegOption<T>[];
}) {
  return (
    <div
      role="tablist"
      style={{
        display: 'inline-flex',
        background: 'var(--surface-strong)',
        border: '1px solid var(--hairline)',
        borderRadius: 999,
        padding: 4,
        gap: 2,
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        boxShadow: 'var(--shadow)',
      }}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            style={{
              minHeight: 38,
              padding: '0 16px',
              borderRadius: 999,
              fontSize: 14,
              fontWeight: 600,
              color: active ? '#000' : 'var(--text)',
              background: active ? '#fff' : 'transparent',
              transition: 'background 100ms ease, color 100ms ease',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
